import React, { useEffect, useRef } from "react";

import { useStatsigClient } from "@statsig/react-bindings";
import LockIcon from "@uui/Lock03";
import { DateTime } from "luxon";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import ReportLoading from "@app/analytics/components/ReportLoading";
import CollapsibleSidebar from "@app/analytics/components/reports/CollapsibleSidebar";
import {
  ContentColumn,
  ContentColumnContainer,
  InnerContainer,
} from "@app/analytics/components/reports/styles";
import UpsellModal from "@app/analytics/components/UpsellModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import MeetingReportBanners from "@app/analytics/MeetingReport/MeetingReportBanners";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import ReportRequestAccess from "@app/analytics/ReportRequestAccess";
import * as AnalyticsEvents from "@app/analytics/tracking/events";
import { getTotalHeight } from "@app/analytics/utils";
import useReportShare from "@app/common/access/ShareReport/hooks/useReportShare";
import { hasRecapOnlyAccess } from "@app/common/access/ShareReport/utils";
import useAuth from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";

import Stack, { Row } from "@rui/Stack";
import Tab from "@rui/Tab";
import Tabs from "@rui/Tabs";

import MeetingRecommendationsBanner from "./components/MeetingRecommendationsBanner";
import EmptyMeetingReport from "./EmptyMeetingReport";
import useExposeToMeet from "./hooks/useExposeToMeet";
import useMeetingReport from "./hooks/useMeetingReport";
import useMeetingReportInitialLoading from "./hooks/useMeetingReportInitialLoading";
import useSessionMetadata from "./hooks/useSessionMetadata";
import MeetingReportContent from "./MeetingReportContent";
import MeetingReportFeedback from "./MeetingReportFeedback";
import MeetingReportHeader, {
  MeetingReportHeaderProps,
} from "./MeetingReportHeader";
import MeetingReportFeedbackSummary from "./MeetingReportHeader/MeetingReportFeedbackSummary";
import MeetingReportNotFound from "./MeetingReportNotFound";
import MeetingReportNudge from "./MeetingReportNudge";
import MeetingReportSidebarContent from "./MeetingReportSidebarContent";
import NotetakerReport from "./NotetakerReport";
import {
  MeetingReportContextProvider,
  useMeetingReportContext,
} from "./providers/MeetingReportContext";
import useTranscriptStore from "./stores/transcript";
import { DisabledOverlay, MeetingReportOuterContainer, TopNav } from "./styles";
import type { MeetingReportTab, SessionMetadata } from "./types";
import VideoPlayer from "./VideoPlayer";

const TABS: {
  label: string;
  value: MeetingReportTab;
  enabled?: (metadata: SessionMetadata) => boolean;
}[] = [
  { label: "Recap", value: "recap" },
  { label: "Deep Dive", value: "deep-dive" },
  {
    label: "Coaching",
    value: "coaching",
    enabled: (metadata) =>
      metadata.currentUserHasCoachingAccess && !metadata.isUploadedSession,
  },
];

const getTabIcon = (
  tab: MeetingReportTab,
  { currentUserCanUpgradeForReport, isRestrictedReport, tier }: SessionMetadata
) => {
  let isLocked = false;

  switch (tab) {
    case "recap":
      isLocked = isRestrictedReport && !currentUserCanUpgradeForReport;
      break;
    case "deep-dive":
      isLocked = isRestrictedReport;
      break;
    case "coaching":
      isLocked = isRestrictedReport || tier === "lite";
      break;
    default:
      break;
  }

  return isLocked ? <LockIcon /> : undefined;
};

const MeetingReport = () => {
  const { client: statsig } = useStatsigClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams();
  const [queryParams] = useSearchParams();
  const { mainScrollContainer, sidebarScrollContainer } =
    useMeetingReportContext();
  const { currentPlan } = useBillingContext();
  const { user } = useAuth();

  const bannerContainerRef = useRef<HTMLDivElement | null>(null);

  const inEditMode = useEditStore((state) => state.editingSection != null);
  const resetAutoFollow = useTranscriptStore((state) => state.resetAutoFollow);
  const [sidebarExpanded, setSidebarExpanded] = useMeetingReportUiStore(
    (state) => [state.sidebar.open, state.setSidebarOpen]
  );

  const {
    isInitialLoading,
    isUnauthorized,
    isNotFound,
    reportQueriesDisabled,
  } = useMeetingReportInitialLoading();

  const { meetingReport } = useMeetingReport({
    enabled: !isUnauthorized,
  });

  const { data: sessionMetadata, updateSessionMetadata } = useSessionMetadata({
    enabled: !isUnauthorized,
  });

  const { accessList } = useReportShare({
    sessionId: sessionId!,
    enabled: !reportQueriesDisabled,
  });

  const currentUserHasRecapOnlyAccess = hasRecapOnlyAccess(
    accessList.currentUserAccessLevel
  );

  useExposeToMeet({ sessionId: sessionId! });

  useEffect(() => {
    statsig.logEvent("meeting_report_view");
  }, [statsig]);

  useEffect(() => {
    if (queryParams.get("action") === "ignoreShare") {
      toast.info("Share request ignored");
    }
  }, [queryParams.get("action")]);

  const enabledTabs = TABS.filter((t) => t.enabled?.(sessionMetadata) ?? true);

  const currentSelectedTab =
    enabledTabs.find((tab) => location.pathname.includes(tab.value)) ??
    enabledTabs[0];

  useEffect(() => {
    if (
      !currentUserHasRecapOnlyAccess ||
      currentSelectedTab.value === "recap"
    ) {
      return;
    }

    // if the user only has recap-only access but somehow navigated straight to
    // a different tab, redirect to the recap tab
    navigate(ROUTES.meetingReportTabById(sessionId!, "recap"), {
      replace: true,
    });
  }, [currentUserHasRecapOnlyAccess]);

  const handleEditTitle = (title: string) => {
    AnalyticsEvents.editName({ entity: "meeting" });
    updateSessionMetadata.mutate(
      { sessionId: sessionId!, title },
      {
        onError: () => {
          toast.error("Error updating title. Please try again.");
        },
      }
    );
  };

  const handleUpdateSessionMetadata = ({
    startTime,
  }: {
    startTime?: DateTime;
  }) => {
    updateSessionMetadata.mutate(
      { sessionId: sessionId!, startTime },
      {
        onError: () => {
          toast.error("Error updating date and time. Please try again.");
        },
      }
    );
  };
  // use the scheduled start time if available for all meeting types, provided the scheduled start time is
  // before the observed end time (i.e., account for cases where someone joined early, left before the call
  // started, and the bot left thinking the meeting was over before it had technically begun)
  const useScheduledStartTime =
    sessionMetadata.scheduledStartTime != null &&
    (!sessionMetadata.endTime ||
      sessionMetadata.scheduledStartTime < sessionMetadata.endTime);

  // uploaded sessions need to always use the session metadata start time, since that's the start
  // time that users give to us on upload. standard bot sessions will use the scheduled start time
  // as applicable, otherwise will try to use the observed start time, and will fall back to the
  // session metadata start time (fallback is necessary for notetaker reports, which don't generate
  // a post-call result)
  const meetingStartTime = sessionMetadata?.isUploadedSession
    ? sessionMetadata.startTime
    : useScheduledStartTime
      ? sessionMetadata.scheduledStartTime
      : meetingReport?.startTime ?? sessionMetadata.startTime;

  const backLocation = location.state?.from
    ? location.state.from
    : ROUTES.MEETING_REPORTS;
  const headerProps: MeetingReportHeaderProps = {
    acl: accessList.currentUserAccessLevel,
    startTime: meetingStartTime,
    endTime:
      !sessionMetadata.isUploadedSession && meetingReport?.endTime
        ? meetingReport?.endTime
        : sessionMetadata.endTime,
    isInitialLoading,
    onBackClick: user ? () => navigate(backLocation) : undefined,
    onEditTitle: handleEditTitle,
    sessionMetadata,
    onEditDateTime: handleUpdateSessionMetadata,
  };

  if (!isInitialLoading) {
    if (isNotFound) {
      return <MeetingReportNotFound />;
    }

    if (isUnauthorized) {
      return <ReportRequestAccess reportId={sessionId!} reportType="meeting" />;
    }

    if (sessionMetadata.isProcessing) {
      return (
        <EmptyMeetingReport
          {...headerProps}
          type={
            sessionMetadata.isProcessingTriggeredByUpgrade
              ? "upgrade_processing"
              : "processing"
          }
        />
      );
    }

    if (sessionMetadata.dataDeleted) {
      return <EmptyMeetingReport {...headerProps} type="data_deleted" />;
    }

    if (sessionMetadata.isNotetakerReport) {
      return <NotetakerReport {...headerProps} />;
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, value: string) => {
    // reset auto-follow on tab change; otherwise changing tabs while the video
    // is playing could cause scrolling chaos
    resetAutoFollow();

    // reset scroll container positions
    if (mainScrollContainer.current) {
      mainScrollContainer.current.scrollTop = 0;
    }

    if (sidebarScrollContainer.current) {
      sidebarScrollContainer.current.scrollTop = 0;
    }

    navigate(
      ROUTES.meetingReportTabById(sessionId!, value as MeetingReportTab)
    );
  };

  const allowShowUpsellModal =
    !isInitialLoading && sessionMetadata.isRestrictedReport;

  const showSidebar =
    !sessionMetadata.isRestrictedReport ||
    (sessionMetadata.currentUserCanUpgradeForReport &&
      currentSelectedTab.value === "recap");

  const heightOffset = getTotalHeight(bannerContainerRef.current);

  if (isInitialLoading) {
    return <ReportLoading />;
  }

  return (
    <>
      <MeetingReportOuterContainer
        withTabs={!currentUserHasRecapOnlyAccess}
        heightOffset={heightOffset}
      >
        <Stack ref={bannerContainerRef}>
          <MeetingReportBanners sessionMetadata={sessionMetadata} />
        </Stack>
        {!isUnauthorized && (
          <>
            <Stack className="meeting-report__header">
              <MeetingReportHeader {...headerProps} />
              <TopNav>
                <Tabs
                  className="meeting-report__top-nav__tabs"
                  value={currentSelectedTab.value}
                  onChange={handleTabChange}
                >
                  {enabledTabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                      icon={getTabIcon(tab.value, sessionMetadata)}
                      iconPosition="end"
                      data-test-id={`meeting-report-tab-${tab.value}`}
                      disabled={inEditMode}
                    />
                  ))}
                </Tabs>
                <Row variant="right" spacing={1}>
                  <MeetingReportFeedbackSummary
                    isMeetingOwner={sessionMetadata.currentUserIsMeetingOwner}
                  />
                  {user && <MeetingRecommendationsBanner />}
                </Row>
              </TopNav>
            </Stack>
            <InnerContainer className="meeting-report__inner-container">
              <ContentColumnContainer>
                <ContentColumn ref={mainScrollContainer}>
                  <MeetingReportContent />
                </ContentColumn>
                <MeetingReportNudge />
              </ContentColumnContainer>
              {showSidebar && (
                <CollapsibleSidebar
                  expanded={sidebarExpanded}
                  onToggleExpand={setSidebarExpanded}
                  overlay={
                    inEditMode ? (
                      <DisabledOverlay tooltip="This part of the report cannot be edited." />
                    ) : null
                  }
                >
                  <VideoPlayer
                    platform={sessionMetadata.meetingPlatform}
                    thumbnailUrl={sessionMetadata?.thumbnailUrl}
                  />
                  <MeetingReportSidebarContent />
                </CollapsibleSidebar>
              )}
            </InnerContainer>
            <MeetingReportFeedback />
          </>
        )}
      </MeetingReportOuterContainer>

      {/* Popups */}
      {allowShowUpsellModal && <UpsellModal currentPlan={currentPlan.type} />}
    </>
  );
};

const MeetingReportWrapper = () => (
  <MeetingReportContextProvider>
    <MeetingReport />
  </MeetingReportContextProvider>
);

export default MeetingReportWrapper;
